import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { readCategoryNames } from '../features/categories/helpers/categoryLanguageHelpers'
import {
  CategoryFeatureOrder,
  CategoryIcon,
  CategorySaveBody,
  CategorySaveResult,
  CategoryWriteReadBack,
  FeatureActiveBody,
  FeatureExample,
  FeatureExamplePosition,
  FeatureExampleSaveResult,
  FeatureSaveBody,
  FeatureSaveResult,
  InternalCategoryRecord,
} from '../features/categories/types/Categories'
import { InternalCategory, InternalCategoryFeature, InternalCategoryMap } from '../types/InternalCategory'
import { legacyAccept, optionalJsonOrText } from './helpers'
import { categoryFeaturesTag, internalApiserverApi } from './internal-apiserver'
import { LanguageMaps } from './language'

// The category and order writes answer with an empty body.
const categoryList = 'categories'
const categoryFeatures = (categoryId: string) => `i/categories/${categoryId}/features`

// The backend answers one example (stored with one market) for every market of a feature, `null` without one, and 500 without the market and store.
const featureExample = (featureId: string, { storeId, marketIso }: FeatureExamplePosition) =>
  `examples/${featureId}?marketIso=${encodeURIComponent(marketIso)}&store=${encodeURIComponent(storeId)}`
const featureExampleTag = (featureId: string) => ({ type: 'internalFeatureExamples' as const, id: featureId })

const categoriesApi = internalApiserverApi
  .enhanceEndpoints({ addTagTypes: ['internalCategories', 'internalCategoryFeatures', 'internalFeatureExamples', 'languageMaps'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /** In the map's own order, which is what legacy shows, not by `ordr`. */
      getInternalCategories: builder.query<InternalCategory[], { marketIso: string }>({
        query: ({ marketIso }) => `i/categories/map/${encodeURIComponent(marketIso)}`,
        transformResponse: (map: InternalCategoryMap) => Object.values(map),
        providesTags: ['internalCategories'],
      }),
      /** Every category, inactive ones included, in the stored order. */
      getInternalCategoryList: builder.query<InternalCategoryRecord[], void>({
        query: () => categoryList,
        transformResponse: (categories: InternalCategoryRecord[]) => [...categories].sort((a, b) => a.ordr - b.ordr),
        providesTags: ['internalCategories'],
      }),
      // Lists inactive features too.
      getInternalCategoryFeatures: builder.query<InternalCategoryFeature[], { categoryId: string }>({
        query: ({ categoryId }) => categoryFeatures(categoryId),
        providesTags: (_result, _error, { categoryId }) => [categoryFeaturesTag(categoryId)],
      }),
      // Creates without an `id` (the answer is the stored category), updates with one (empty answer). The backend writes
      // `translations` into the `categories` language group under the category's legacy id.
      saveInternalCategory: builder.mutation<CategorySaveResult, CategorySaveBody>({
        async queryFn(category, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({
            url: 'i/categories',
            method: category.id ? 'PUT' : 'POST',
            body: category,
            headers: legacyAccept,
            responseHandler: optionalJsonOrText,
          })
          if (saved.error) return { error: saved.error }
          const [categories, languageMaps] = await Promise.all([baseQuery(categoryList), baseQuery('language')])
          if (categories.error || languageMaps.error) return { error: (categories.error ?? languageMaps.error) as FetchBaseQueryError }
          const id = category.id ?? (saved.data as InternalCategoryRecord | null)?.id
          const stored = id ? (categories.data as InternalCategoryRecord[]).find((candidate) => candidate.id === id) : undefined
          return { data: { stored, storedNames: stored ? readCategoryNames(languageMaps.data as LanguageMaps, stored.legacyId) : undefined } }
        },
        invalidatesTags: ['internalCategories', 'languageMaps'],
      }),
      // Saves the whole feature, so the body carries its translations and its choices' translations (legacy's toggle).
      toggleInternalFeatureActive: builder.mutation<CategoryWriteReadBack, FeatureActiveBody>({
        async queryFn(feature, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'i/features', method: 'PUT', body: feature, headers: legacyAccept, responseHandler: optionalJsonOrText })
          if (saved.error) return { error: saved.error }
          const features = await baseQuery(categoryFeatures(feature.categoryId))
          if (features.error) return { error: features.error }
          return { data: { stored: features.data as InternalCategoryFeature[] } }
        },
        invalidatesTags: (_result, _error, { categoryId }) => [categoryFeaturesTag(categoryId), 'internalFeatures', 'internalCategories', 'languageMaps'],
      }),
      saveInternalCategoryFeatureOrder: builder.mutation<CategoryWriteReadBack, { categoryId: string; order: CategoryFeatureOrder }>({
        async queryFn({ categoryId, order }, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({
            url: `i/categories/order/${categoryId}`,
            method: 'PUT',
            body: order,
            headers: legacyAccept,
            responseHandler: optionalJsonOrText,
          })
          if (saved.error) return { error: saved.error }
          const features = await baseQuery(categoryFeatures(categoryId))
          if (features.error) return { error: features.error }
          return { data: { stored: features.data as InternalCategoryFeature[] } }
        },
        invalidatesTags: (_result, _error, { categoryId }) => [categoryFeaturesTag(categoryId)],
      }),
      // Creates without an `id` (the answer is the stored feature, whose id the examples need), updates with one. The backend writes
      // `translations` into the `features` and `choices` language groups.
      saveInternalFeature: builder.mutation<FeatureSaveResult, FeatureSaveBody>({
        async queryFn(feature, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({
            url: 'i/features',
            method: feature.id ? 'PUT' : 'POST',
            body: feature,
            headers: legacyAccept,
            responseHandler: optionalJsonOrText,
          })
          if (saved.error) return { error: saved.error }
          const id = feature.id ?? (saved.data as { id?: string } | null)?.id
          const [features, languageMaps] = await Promise.all([baseQuery(categoryFeatures(feature.categoryId)), baseQuery('language')])
          if (features.error || languageMaps.error) return { error: (features.error ?? languageMaps.error) as FetchBaseQueryError }
          const stored = id ? (features.data as InternalCategoryFeature[]).find((candidate) => candidate.id === id) : undefined
          return { data: { id, stored, languageMaps: languageMaps.data as LanguageMaps } }
        },
        invalidatesTags: (_result, _error, { categoryId }) => [categoryFeaturesTag(categoryId), 'internalFeatures', 'internalCategories', 'languageMaps'],
      }),
      getInternalFeatureExample: builder.query<FeatureExample | null, { featureId: string; position: FeatureExamplePosition }>({
        query: ({ featureId, position }) => ({ url: featureExample(featureId, position), responseHandler: optionalJsonOrText }),
        providesTags: (_result, _error, { featureId }) => [featureExampleTag(featureId)],
      }),
      // Updates with an `id`, creates without; read back at the example's position.
      saveInternalFeatureExample: builder.mutation<
        FeatureExampleSaveResult,
        { example: FeatureExample & { featureId: string }; position: FeatureExamplePosition }
      >({
        async queryFn({ example, position }, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({
            url: 'i/examples',
            method: example.id ? 'PUT' : 'POST',
            body: example,
            headers: legacyAccept,
            responseHandler: optionalJsonOrText,
          })
          if (saved.error) return { error: saved.error }
          const stored = await baseQuery({ url: featureExample(example.featureId, position), responseHandler: optionalJsonOrText })
          if (stored.error) return { error: stored.error }
          return { data: { stored: (stored.data as FeatureExample | null) ?? null } }
        },
        invalidatesTags: (_result, _error, { example }) => [featureExampleTag(example.featureId)],
      }),
      // Uploaded files can't be deleted.
      uploadInternalCategoryIcon: builder.mutation<CategoryIcon, File>({
        query: (file) => {
          const body = new FormData()
          body.append('file', file)
          body.append('type', 'categoryIcon')
          return { url: 'files/upload/private', method: 'POST', body }
        },
      }),
    }),
  })

export const {
  useGetInternalCategoriesQuery,
  useGetInternalCategoryListQuery,
  useGetInternalCategoryFeaturesQuery,
  useSaveInternalCategoryMutation,
  useToggleInternalFeatureActiveMutation,
  useSaveInternalCategoryFeatureOrderMutation,
  useUploadInternalCategoryIconMutation,
  useSaveInternalFeatureMutation,
  useLazyGetInternalFeatureExampleQuery,
  useSaveInternalFeatureExampleMutation,
} = categoriesApi
